/**
 * Set type from the Pages tree in the browser: the shared picker and sequence
 * (loaded when someone picks Set type) over `webSetPageTypeEffects`. A result
 * short of success is said in a notice above the page; nothing is shown as a
 * native dialog.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react';
import {
  loadSetPageType,
  useCollabDocsUI,
  useCollabPagesState,
  type SharedDocument,
} from '@nimbalyst/collab-bundle/docs-ui';
import { useTrackersUI } from '@nimbalyst/collab-bundle/trackers-ui';
import type { TeamJwt, TeamSession } from '../auth/teamJwt';
import { orgPageItemPath, type RouteTeam } from '../routing';
import { flushOpenPageEditor } from './openPageEditors';
import { usePagesTrackers } from './PagesTrackers';
import { buildWebSetPageTypeDependencies, type SetTypeTrackerCommand } from './webSetPageTypeEffects';

type PageTypes = Awaited<ReturnType<typeof loadSetPageType>>;

export interface PagesSetTypeOptions {
  team: RouteTeam;
  projectId: string;
  teamSession: TeamSession;
  getTeamJwt: () => Promise<TeamJwt>;
  serverUrl: string;
  navigate: (path: string) => void;
}

export interface PagesSetType {
  /** Undefined until the tracker room is open, which shows Set type disabled. */
  onSetPageType?: (document: SharedDocument) => void;
  /** The picker while open, and the last failure. */
  element: ReactNode;
}

async function readRoomMarkdown(options: PagesSetTypeOptions, documentId: string): Promise<string> {
  const { openBrowserDocumentRoom, readDocumentRoomMarkdown } = await import('@nimbalyst/collab-bundle/editor');
  return readDocumentRoomMarkdown(openBrowserDocumentRoom({
    serverUrl: options.serverUrl,
    orgId: options.teamSession.orgId,
    documentId,
    // The editor and the console brand these separately; the values are the same checked ids.
    teamMemberId: options.teamSession.memberId as never,
    getTeamJwt: options.getTeamJwt as never,
  }));
}

export function usePagesSetType(options: PagesSetTypeOptions): PagesSetType {
  const { session } = useCollabDocsUI();
  const { typePlacements } = useCollabPagesState();
  const { resolver, defineType } = usePagesTrackers();
  const { dataSource, dataStore } = useTrackersUI();
  const [picking, setPicking] = useState<SharedDocument | null>(null);
  const [pageTypes, setPageTypes] = useState<PageTypes | null>(null);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // "New type..." from the picker replaces it until closed; the picker then lists the new type.
  const [creatingType, setCreatingType] = useState(false);
  const latest = useRef({ options, typePlacements });
  latest.current = { options, typePlacements };

  const open = useCallback((document: SharedDocument) => {
    setNotice(null);
    setPicking(document);
    void loadSetPageType().then(setPageTypes, (error: unknown) => {
      setPicking(null);
      setNotice(`Set type could not load: ${error instanceof Error ? error.message : String(error)}`);
    });
  }, []);

  const run = useCallback(async (page: SharedDocument, typeId: string) => {
    if (!pageTypes || !dataSource || !dataStore) return;
    const current = latest.current.options;
    setRunning(true);
    try {
      const title = page.title.trim() || 'Untitled';
      const outcome = await pageTypes.setPageType(
        {
          lane: 'team',
          typeId,
          page: {
            documentId: page.documentId,
            title,
            documentType: page.documentType,
            parentId: page.parentFolderId ?? null,
            parentKind: page.parentKind ?? 'page',
            sortOrder: page.sortOrder ?? null,
          },
        },
        buildWebSetPageTypeDependencies({
          workspace: dataSource.status().workspacePath,
          session,
          typePlacements: () => latest.current.typePlacements,
          pageTypes,
          itemInput: pageTypes.browserPageTypeItemInput,
          readDocumentMarkdown: (documentId) => readRoomMarkdown(current, documentId),
          trackerCommand: (command: SetTypeTrackerCommand) => dataStore.command(command),
          itemSynced: (itemId) => dataStore.getSnapshot().recordsById.get(itemId)?.syncStatus === 'synced',
          subscribeItems: (listener) => dataStore.subscribe(listener),
          flushPageEditor: flushOpenPageEditor,
          openItem: (itemId) => current.navigate(orgPageItemPath(current.team, current.projectId, itemId)),
          wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
        }),
      );
      if (outcome.status !== 'done') setNotice(outcome.message);
    } finally {
      setRunning(false);
      setPicking(null);
    }
  }, [dataSource, dataStore, pageTypes, session]);

  const Dialog = pageTypes?.SetPageTypeDialog;
  const NewTypeDialog = pageTypes?.NewTypeDialog;
  const element = (
    <>
      {picking && !creatingType && Dialog && resolver ? (
        <Dialog
          pageTitle={picking.title}
          resolver={resolver}
          running={running}
          onPick={(typeId) => { void run(picking, typeId); }}
          onClose={() => setPicking(null)}
          onNewType={defineType ? () => setCreatingType(true) : undefined}
        />
      ) : null}
      {picking && creatingType && NewTypeDialog && resolver && defineType ? (
        <NewTypeDialog
          lane="team"
          resolver={resolver}
          session={session}
          defineType={defineType}
          onClose={() => setCreatingType(false)}
        />
      ) : null}
      {notice ? (
        <div className="pages-set-type-notice flex items-start gap-2 border-b border-nim bg-nim-secondary px-4 py-2 text-xs text-nim" role="alert">
          <span className="flex-1">{notice}</span>
          <button type="button" className="text-nim-muted hover:text-nim" onClick={() => setNotice(null)}>Dismiss</button>
        </div>
      ) : null}
    </>
  );
  return { onSetPageType: resolver && dataStore ? open : undefined, element };
}
